import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, App, Button, Empty, Input, Space, Table, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { UserDictionaryEntry, UserDictionaryValidateResponse } from '../../../lib/api/searchDictionary';
import { DictSection } from './DictSection';
import { searchDictionaryQuery } from '../queries';
import { useValidateUserDictionaryMutation } from '../hooks/useSynonymMutations';
import { readApiError } from '../utils/apiError';

const toLine = (entry: UserDictionaryEntry): string => [entry.headword, ...entry.segments].join(' ');

/**
 * 사용자사전 (D-①-5) — 원본은 레포 파일이라 화면은 **조회 · 검증 · 초안** 까지만 한다.
 * 반영은 커밋 → 배포 → 재색인. 재색인 버튼은 두지 않는다(prod 는 HTTP 재색인이 막혀 있고 8분이 걸린다).
 *
 * 🔴 검증이 이 화면의 핵심이다(D-①-6 역방향): 표제어 한 줄이 기존 동의어 토큰의 분석을 바꿔
 *    동의어 세트를 깨뜨릴 수 있다 — 과거 「사전 충돌로 검색 먹통」 사고가 그 경로였다.
 */
export const UserDictionaryPanel = (): React.JSX.Element => {
  const { message } = App.useApp();
  const query = useQuery(searchDictionaryQuery.userDictionary());
  const validate = useValidateUserDictionaryMutation();
  const [keyword, setKeyword] = useState('');
  const [draft, setDraft] = useState('');
  /** 검증 결과는 검증한 파일 전체(기준 사전 + 초안)와 묶어 둔다 */
  const [checked, setChecked] = useState<{ text: string; response: UserDictionaryValidateResponse } | null>(null);

  const entries = useMemo(() => query.data?.entries ?? [], [query.data]);
  const filtered = useMemo(() => {
    const key = keyword.trim().toLowerCase();
    return key ? entries.filter((entry) => toLine(entry).toLowerCase().includes(key)) : entries;
  }, [entries, keyword]);

  const draftLines = draft
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  /** 초안 = 지금 파일 + 새로 넣을 줄. 서버는 파일 전체를 받아 중복·역방향을 본다 */
  const fullDraft = [...entries.map(toLine), ...draftLines];
  const fullDraftText = `${fullDraft.join('\n')}\n`;
  // 지금 파일과 같은 조합을 검증한 결과만 쓴다 — 기준 사전을 새로 불러왔거나 초안을 고쳤으면, 늦게 온 응답이어도 버린다
  const result = checked?.text === fullDraftText ? checked.response : null;

  // 지금 사전을 다 불러온 뒤에만 검증·복사한다 — 비어 있는 채로 검증하면 겹침을 못 잡고, 복사본엔 새 줄만 남는다
  const isBaseReady = query.isSuccess;

  const runValidate = async (): Promise<void> => {
    if (!isBaseReady) return;
    const text = fullDraftText;
    setChecked(null);
    try {
      setChecked({ text, response: await validate.mutateAsync(fullDraft) });
    } catch (error) {
      void message.error(readApiError(error, '검증하지 못했습니다.').message);
    }
  };

  const copyDraft = async (): Promise<void> => {
    // 통과한 그 파일 그대로만 복사한다
    if (!checked || !result?.passed) return;
    try {
      await navigator.clipboard.writeText(checked.text);
      void message.success('초안을 복사했습니다. 레포의 사전 파일에 붙여 넣어 커밋해 주세요.');
    } catch {
      void message.error('복사하지 못했습니다. 브라우저 권한을 확인해 주세요.');
    }
  };

  const columns: ColumnsType<UserDictionaryEntry> = [
    { title: '줄', dataIndex: 'line', width: 70, align: 'center' },
    { title: '표제어', dataIndex: 'headword', width: 220 },
    {
      title: '나뉘는 말',
      dataIndex: 'segments',
      render: (segments: string[]) =>
        segments.length ? segments.join(' · ') : <span className="settings-muted">나누지 않음</span>,
    },
  ];

  return (
    <div className="settings-stack">
      <Alert
        className="settings-inline-alert"
        type="warning"
        showIcon
        message="사용자사전은 레포 파일이 원본입니다."
        description="이 화면에서는 조회와 검증, 초안 작성까지 합니다. 바뀐 내용은 커밋·배포한 뒤 재색인을 해야 검색에 반영됩니다."
      />
      <div className="settings-two-columns">
        <DictSection
          title="지금 사전"
          description={query.data ? `${entries.length}줄 · 기준 ${query.data.source} (배포된 버전)` : undefined}
        >
          <Input
            aria-label="표제어 찾기"
            placeholder="표제어로 찾기"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            allowClear
            style={{ marginBottom: 16, maxWidth: 320 }}
          />
          {query.isError ? (
            <Alert
              type="error"
              showIcon
              message="사용자사전을 불러오지 못했습니다."
              action={
                <Button loading={query.isFetching} onClick={() => query.refetch()}>
                  다시 시도
                </Button>
              }
            />
          ) : (
            <Table
              rowKey="line"
              size="small"
              columns={columns}
              dataSource={filtered}
              loading={query.isLoading}
              pagination={{ pageSize: 20, showSizeChanger: false, hideOnSinglePage: true }}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="표제어가 없습니다." /> }}
            />
          )}
        </DictSection>

        <DictSection
          title="초안 검증"
          description="넣을 표제어를 한 줄에 하나씩 적습니다. 나눌 말이 있으면 띄어 씁니다 (예: 시그넷링 시그넷 링)."
        >
          <div className="field-stack">
            <label className="settings-label" htmlFor="user-dictionary-draft">
              넣을 표제어
            </label>
            <Input.TextArea
              id="user-dictionary-draft"
              autoSize={{ minRows: 4, maxRows: 10 }}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
            />
            <Space>
              <Button
                type="primary"
                disabled={!isBaseReady || !draftLines.length}
                loading={validate.isLoading}
                onClick={() => void runValidate()}
              >
                검증
              </Button>
              <Button disabled={!isBaseReady || !result?.passed} onClick={() => void copyDraft()}>
                초안 복사
              </Button>
            </Space>
            {result && (
              <div className="rule-feedback">
                {result.passed ? (
                  <Alert type="success" showIcon message="통과 · 표제어 겹침 없음 · 막히는 동의어 없음" />
                ) : (
                  <Alert type="error" showIcon message="이대로는 반영할 수 없습니다." />
                )}
                {result.duplicates.map((duplicate) => (
                  <Alert
                    key={duplicate.headword}
                    type="error"
                    showIcon
                    message={`표제어 「${duplicate.headword}」 가 ${duplicate.lines.join(', ')}번째 줄에 겹칩니다. 검색엔진이 사전을 받아 주지 않습니다.`}
                  />
                ))}
                {result.truncated && (
                  <Alert
                    type="warning"
                    showIcon
                    message="확인할 동의어가 너무 많아 일부만 검사했습니다. 표제어를 나눠서 검증해 주세요."
                  />
                )}
                {result.affectedSynonyms.map((affected) => (
                  <Alert
                    key={`${affected.ruleId}-${affected.term}`}
                    type={affected.blocked ? 'error' : 'warning'}
                    showIcon
                    message={
                      <>
                        동의어 토큰 「{affected.term}」 의 나뉘는 말이 바뀝니다{' '}
                        <Tag>{affected.before.join(' · ') || '없음'}</Tag>→
                        <Tag>{affected.after.join(' · ') || '없음'}</Tag>
                        {affected.blocked && ' — 이 동의어를 쓸 수 없게 됩니다.'}
                      </>
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </DictSection>
      </div>
    </div>
  );
};
